import { api } from './api'
import type {
  BillingAccount,
  BillingAccountFormData,
  BillingAccountSummary,
  BillableItem,
  BillableItemFormData,
  BillingPosItem,
  BillingPayment,
  BillingPaymentFormData,
  BillingStatement,
  BillingStatementFormData,
  BillingPaymentMethod,
  BillingPaymentMethodFormData,
} from '../types/billing'

export interface BillingAccountQuery {
  patientId?: string
}

export interface BillableItemQuery {
  patientId?: string
  accountId?: string
  consultationId?: string
  status?: string
}

export interface BillingPaymentQuery {
  patientId?: string
  accountId?: string
}

export interface BillingStatementQuery {
  patientId?: string
  accountId?: string
}

export interface BillingPaymentMethodQuery {
  patientId?: string
  accountId?: string
}

export const billingService = {
  getAccounts: async (params?: BillingAccountQuery): Promise<BillingAccount[]> => {
    const response = await api.get<BillingAccount[]>('/billing/accounts', { params })
    return response.data
  },

  getAccountById: async (id: string): Promise<BillingAccount> => {
    const response = await api.get<BillingAccount>(`/billing/accounts/${id}`)
    return response.data
  },

  getAccountSummary: async (id: string): Promise<BillingAccountSummary> => {
    const response = await api.get<BillingAccountSummary>(`/billing/accounts/${id}/summary`)
    return response.data
  },

  createAccount: async (data: BillingAccountFormData): Promise<BillingAccount> => {
    const response = await api.post<BillingAccount>('/billing/accounts', data)
    return response.data
  },

  updateAccount: async (id: string, data: Partial<BillingAccountFormData>): Promise<BillingAccount> => {
    const response = await api.put<BillingAccount>(`/billing/accounts/${id}`, data)
    return response.data
  },

  getItems: async (params?: BillableItemQuery): Promise<BillableItem[]> => {
    const response = await api.get<BillableItem[]>('/billing/items', { params })
    return response.data
  },

  getItemById: async (id: string): Promise<BillableItem> => {
    const response = await api.get<BillableItem>(`/billing/items/${id}`)
    return response.data
  },

  getItemsByConsultation: async (consultationId: string): Promise<BillableItem[]> => {
    const response = await api.get<BillableItem[]>(`/billing/consultations/${consultationId}/items`)
    return response.data
  },

  createItem: async (data: BillableItemFormData): Promise<BillableItem> => {
    const response = await api.post<BillableItem>('/billing/items', data)
    return response.data
  },

  createConsultationItem: async (consultationId: string, data: BillableItemFormData): Promise<BillableItem> => {
    const response = await api.post<BillableItem>(`/billing/consultations/${consultationId}/items`, data)
    return response.data
  },

  updateItem: async (id: string, data: Partial<BillableItemFormData>): Promise<BillableItem> => {
    const response = await api.put<BillableItem>(`/billing/items/${id}`, data)
    return response.data
  },

  deleteItem: async (id: string): Promise<void> => {
    await api.delete(`/billing/items/${id}`)
  },

  attachItemToConsultation: async (consultationId: string, itemId: string): Promise<BillableItem> => {
    const response = await api.post<BillableItem>(`/billing/consultations/${consultationId}/items/${itemId}`)
    return response.data
  },

  detachItemFromConsultation: async (consultationId: string, itemId: string): Promise<BillableItem> => {
    await api.delete<BillableItem>(`/billing/consultations/${consultationId}/items/${itemId}`)
    return billingService.getItemById(itemId)
  },

  getPayments: async (params?: BillingPaymentQuery): Promise<BillingPayment[]> => {
    const response = await api.get<BillingPayment[]>('/billing/payments', { params })
    return response.data
  },

  getPaymentById: async (id: string): Promise<BillingPayment> => {
    const response = await api.get<BillingPayment>(`/billing/payments/${id}`)
    return response.data
  },

  createPayment: async (data: BillingPaymentFormData): Promise<BillingPayment> => {
    const response = await api.post<BillingPayment>('/billing/payments', data)
    return response.data
  },

  getStatements: async (params?: BillingStatementQuery): Promise<BillingStatement[]> => {
    const response = await api.get<BillingStatement[]>('/billing/statements', { params })
    return response.data
  },

  getStatementById: async (id: string): Promise<BillingStatement> => {
    const response = await api.get<BillingStatement>(`/billing/statements/${id}`)
    return response.data
  },

  createStatement: async (data: BillingStatementFormData): Promise<BillingStatement> => {
    const response = await api.post<BillingStatement>('/billing/statements', data)
    return response.data
  },

  getPaymentMethods: async (params?: BillingPaymentMethodQuery): Promise<BillingPaymentMethod[]> => {
    const response = await api.get<BillingPaymentMethod[]>('/billing/payment-methods', { params })
    return response.data
  },

  createPaymentMethod: async (data: BillingPaymentMethodFormData): Promise<BillingPaymentMethod> => {
    const response = await api.post<BillingPaymentMethod>('/billing/payment-methods', data)
    return response.data
  },

  deletePaymentMethod: async (id: string): Promise<void> => {
    await api.delete(`/billing/payment-methods/${id}`)
  },

  getPosItems: async (): Promise<BillingPosItem[]> => {
    const response = await api.get<BillingPosItem[]>('/billing/pos-items')
    return response.data
  },
}
